import { Button, Checkbox, CheckboxOptionType, Col, Form, Input, Radio, RadioChangeEvent, Row, Select, notification } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import RadioBtn from '../../Common/Form/RadioBtn';
import { DivWrapper, StyledForm } from './style';
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useEffect, useMemo, useState } from 'react';
import { AllergiesType } from '../../../types';
import useListOptionsForAllergiesAndOthers from '../../../hooks/common/useListOptions';
import CustomLoading from '../../Common/Loading';
import { useUpdateProfessionalClientAllergies } from '../../../hooks/professionals/useClientGeneralInfo';
import { useUpdateClientAllergies } from '../../../hooks/client/useClientGeneralInfo';
import { NotificationTitle } from '../../Common/Notification/style';
import { theme } from '../../../styles/theme';
import { useGetProfessionalClient } from '../../../hooks/professionals/useProfessionalClient';

const { TextArea } = Input;

const generateUpdatedAttributes = (
  selectedItems: any[],
  checkedItems: any[],
  clientKey: string,
  mutationKey: string
) => {

  // Update existing items in the array
  const updatedItems = selectedItems?.map((item: any) => {
    const isChecked = checkedItems.includes(item[clientKey].id);
    return {
      id: item.id,
      [mutationKey]: item[clientKey].id,
      _destroy: !isChecked,
    };
  });

  // Add new checked items that are not in the original array
  const newItems = checkedItems
    .filter(
      (value) => !selectedItems?.some((item: any) => item[clientKey].id === value)
    )
    .map((newItemId) => ({
      [mutationKey]: newItemId,
      _destroy: false,
    }));

  return [...updatedItems, ...newItems];
};


const AllergiesAndOthersForm = () => {
  const [api, contextHolder] = notification.useNotification();
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const { user } = useSelector((state: RootState) => state.auth);
  const isClient = !!user && "professionalCode" in user;
  const { data: listOptions, loading: loadingListOptions } = useListOptionsForAllergiesAndOthers();
  const { update: updateAllergies, loading: loadingAllergies } = useUpdateProfessionalClientAllergies();
  const { update: updateClientAllergies, loading: loadingClientAllergies } = useUpdateClientAllergies();
  const { refetch: refetchClient } = useGetProfessionalClient(selectedClient?.id as string);
  const [withFoodSupplement, setWithFoodSupplement] = useState<Boolean>(Boolean(selectedClient?.foodSupplement));
  const [withAllergies, setWithAllergies] = useState<Boolean>(Boolean(selectedClient?.allergies));
  const [withFoodIntolerances, setWithFoodIntolerances] = useState<Boolean>(Boolean(selectedClient?.foodIntolerances));
  const [withAddictions, setWithAddictions] = useState<Boolean>(Boolean(selectedClient?.addictions));
  const [isSmokingCigar, setIsSmokingCigar] = useState<Boolean>(Boolean(selectedClient?.cigaretteConsumption));
  const [checkedAllergies, setCheckedAllergies] = useState(
    selectedClient?.allergies?.map((allergy: AllergiesType) => allergy.allergy.id) || []
  );
  const [checkedFoodIntolerances, setCheckedFoodIntolerances] = useState(
    selectedClient?.foodIntolerances?.map((foodIntolerance: any) => foodIntolerance.foodIntolerance.id) || []
  );
  const [checkedAddictions, setCheckedAddictions] = useState(
    selectedClient?.addictions?.map((addiction: any) => addiction.addiction.id) || []
  );

  const onChangeAllergies = (checkedValues: any) => {
    setCheckedAllergies(checkedValues);
  };

  const onChangeFoodIntolerances = (checkedValues: any) => {
    setCheckedFoodIntolerances(checkedValues);
  };

  const onChangeAddictions = (checkedValues: any) => {
    setCheckedAddictions(checkedValues);
  };

  const [form] = Form.useForm();

  const allergiesOptions = useMemo(() => {
    if (listOptions) {
      return listOptions?.allergies?.map((option: any) => ({
        value: option.id,
        label: option.name
      }));
    }
  }, [listOptions]);

  const foodIntolerancesOptions = useMemo(() => {
    if (listOptions) {
      return listOptions?.foodIntolerances?.map((option: any) => ({
        value: option.id,
        label: option.name
      }));
    }
  }, [listOptions]);

  const addictionsOptions = useMemo(() => {
    if (listOptions) {
      return listOptions?.addictions?.map((option: any) => ({
        value: option.id,
        label: option.name
      }));
    }
  }, [listOptions]);

  const onFinish = async (values: any) => {
    const updatedAllergies = generateUpdatedAttributes(
      selectedClient?.allergies || [],
      checkedAllergies,
      'allergy',
      'optionAllergyId'
    );

    const updatedFoodIntolerances = generateUpdatedAttributes(
      selectedClient?.foodIntolerances || [],
      checkedFoodIntolerances,
      'foodIntolerance',
      'optionFoodIntoleranceId'
    );

    const updatedAddictions = generateUpdatedAttributes(
      selectedClient?.addictions || [],
      checkedAddictions,
      'addiction',
      'optionAddictionId'
    );

    const requestForHelpAttributes = {
      ...(values.requestForHelpAttributes?.id || selectedClient?.requestForHelp?.id ? { id: values.requestForHelpAttributes?.id || selectedClient?.requestForHelp?.id } : {}),
      description: values.requestForHelpAttributes?.description || 'None' // fallback to non-blank string to pass the typing correctly
    };

    const foodSupplementAttributes = withFoodSupplement
      ? {
        ...(values.foodSupplementAttributes?.id || selectedClient?.foodSupplement?.id ? { id: values.foodSupplementAttributes?.id || selectedClient?.foodSupplement?.id } : {}),
        content: values.foodSupplementAttributes?.content || 'None'
      }
      : {
        ...(selectedClient?.foodSupplement?.id ? { id: selectedClient.foodSupplement.id } : {}),
        content: 'None'
      };

    const cigaretteConsumptionAttributes = isSmokingCigar
      ? {
        ...(values.cigaretteConsumptionAttributes?.id || selectedClient?.cigaretteConsumption?.id ? { id: values.cigaretteConsumptionAttributes?.id || selectedClient?.cigaretteConsumption?.id } : {}),
        frequency: values.cigaretteConsumptionAttributes?.frequency || 'Less than 1 per day'
      }
      : {
        ...(selectedClient?.cigaretteConsumption?.id ? { id: selectedClient.cigaretteConsumption.id } : {}),
        frequency: 'Less than 1 per day'
      };

    const payload = {
      ...values,
      allergiesAttributes: updatedAllergies,
      foodIntolerancesAttributes: updatedFoodIntolerances,
      addictionsAttributes: updatedAddictions,
      allergyMiscAttributes: {
        ...(selectedClient?.allergyMisc?.id ? { id: selectedClient.allergyMisc.id } : {}),
        other: selectedClient?.allergyMisc?.other || 'None'
      },
      foodIntoleranceMiscAttributes: {
        ...(selectedClient?.foodIntoleranceMisc?.id ? { id: selectedClient.foodIntoleranceMisc.id } : {}),
        other: selectedClient?.foodIntoleranceMisc?.other || 'None'
      },
      requestForHelpAttributes,
      foodSupplementAttributes,
      cigaretteConsumptionAttributes
    };

    let res;
    if (selectedClient && !isClient) {
      res = await updateAllergies(selectedClient?.id, payload);
      console.log('res', res)
    } else if (isClient) {
      res = await updateClientAllergies(payload);
      console.log('res', res)
    }

    if (res?.professionalClientUpdateAllergies || res?.clientUpdateAllergies) {
      await refetchClient();
      api.info({
        message: <NotificationTitle>Allergies updates successfully</NotificationTitle>,
        placement: 'topRight',
        icon:
          <CheckCircleOutlined
            style={{
              color: theme.color.primary,
            }}
          />,
        style: {
          background: `${theme.bg.secondary}`,
          borderRadius: 10,
          alignItems: 'center',
          display: 'flex'
        }
      });
    }

    console.log('payload', payload)
  };

  useEffect(() => {
    form.setFieldsValue({
      requestForHelpAttributes: selectedClient?.requestForHelp,
      foodSupplementAttributes: selectedClient?.foodSupplement,
      cigaretteConsumptionAttributes: selectedClient?.cigaretteConsumption
    });
  }, [form, selectedClient]);

  if (loadingListOptions) {
    return <CustomLoading />
  }

  return (
    <StyledForm
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="allergiesAndOthers"
      onFinish={onFinish}
      initialValues={{}}
      style={{ marginTop: '1rem' }}
      scrollToFirstError
    >
      {contextHolder}
      <p>What is your request for help?</p>
      <Form.Item name={["requestForHelpAttributes", "id"]} hidden>
        <Input />
      </Form.Item>
      <Form.Item name={["requestForHelpAttributes", "description"]}>
        <TextArea rows={4} />
      </Form.Item>
      <p>Do you use food supplements?</p>
      <Radio.Group
        options={[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]}
        onChange={(e: RadioChangeEvent) => setWithFoodSupplement(e.target.value)}
        value={withFoodSupplement}
        optionType="button"
        buttonStyle="solid"
        style={{ marginBottom: 10 }}
      />
      {withFoodSupplement && (
        <>
          <Form.Item name={["foodSupplementAttributes", "id"]} hidden>
            <Input />
          </Form.Item>
          <Form.Item name={["foodSupplementAttributes", "content"]}>
            <TextArea rows={4} />
          </Form.Item>
        </>
      )}
      <p>Do you have allergies?</p>
      <Radio.Group
        onChange={(e: RadioChangeEvent) => setWithAllergies(e.target.value)}
        options={[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]}
        value={withAllergies}
        optionType="button"
        buttonStyle="solid"
      />
      {withAllergies && (
        <DivWrapper>
          <Checkbox.Group value={checkedAllergies} onChange={onChangeAllergies}>
            <Row>
              {allergiesOptions?.map((option: any) => (
                <Col span={12}>
                  <Checkbox value={option.value}>{option.label}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </DivWrapper>
      )}
      <p>Do you have food intolerances?</p>
      <Radio.Group
        onChange={(e: RadioChangeEvent) => setWithFoodIntolerances(e.target.value)}
        options={[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]}
        value={withFoodIntolerances}
        optionType="button"
        buttonStyle="solid"
      />
      {withFoodIntolerances && (
        <DivWrapper>
          <Checkbox.Group value={checkedFoodIntolerances} onChange={onChangeFoodIntolerances}>
            <Row>
              {foodIntolerancesOptions?.map((option: any) => (
                <Col span={12}>
                  <Checkbox value={option.value}>{option.label}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </DivWrapper>
      )}
      <p>Do you have any addictions?</p>
      <Radio.Group
        onChange={(e: RadioChangeEvent) => setWithAddictions(e.target.value)}
        options={[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]}
        value={withAddictions}
        optionType="button"
        buttonStyle="solid"
      />
      {withAddictions && (
        <DivWrapper>
          <Checkbox.Group value={checkedAddictions} onChange={onChangeAddictions}>
            <Row>
              {addictionsOptions?.map((option: any) => (
                <Col span={12}>
                  <Checkbox value={option.value}>{option.label}</Checkbox>
                </Col>
              ))}
            </Row>
          </Checkbox.Group>
        </DivWrapper>
      )}
      <p>Do you smoke cigarettes / cigars?</p>
      <Radio.Group
        onChange={(e: RadioChangeEvent) => setIsSmokingCigar(e.target.value)}
        options={[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]}
        value={isSmokingCigar}
        optionType="button"
        buttonStyle="solid"
      />
      {isSmokingCigar && (
        <DivWrapper>
          <label>How many cigarettes / cigars do you smoke per day?</label>
          <Form.Item name={["cigaretteConsumptionAttributes", "frequency"]}>
            <Select>
              <Select.Option value="Less than 1 per day">Less than 1 per day</Select.Option>
              <Select.Option value="1-4">1-4</Select.Option>
              <Select.Option value="5-10">5-10</Select.Option>
              <Select.Option value="More than 10">More than 10</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name={["cigaretteConsumptionAttributes", "id"]} hidden>
            <Input />
          </Form.Item>
        </DivWrapper>
      )}
      <Form.Item {..._TAIL_FORM_ITEM_LAYOUT}>
        <Button type="primary" htmlType="submit" loading={isClient ? loadingClientAllergies : loadingAllergies}>
          Save
        </Button>
      </Form.Item>
    </StyledForm>
  )
};

export default AllergiesAndOthersForm;